import Button from '@components/Button';
import Checkbox, { CheckboxNoFunc } from '@components/Checkbox';
import { Collapse } from '@components/Collapse';
import FeatureToggle from '@components/FeatureToggle';
import Image from '@components/Image';
import ImageContainer from '@components/ImageContainer';
import { ImgSVG } from '@components/ImgSVG';
import Input from '@components/Input';
import Tip from '@components/Tip';
import Title from '@components/Title';
import { Validator } from '@components/Validator';
import useInputHandlers from '@hooks/useInputHandelrs';
import useTourEditing from '@hooks/useTourEditing';
import {
  sandtableGroupIconList,
  selectLibSandtableGroupIcons,
} from '@modules/mediaLib';
import { viewDefault } from '@modules/view';
import { $tourDataApi } from '@stores/dataStores';
import { $GateSandtableGroup } from '@stores/editingMarkerStores';
import {
  ImageType,
  type KrpPlayer,
  type MatrixFov,
  MatrixFovType,
  type PanoMultires,
  type STImage,
  type STImageMarker,
  type STMatrix,
  type SandTableImageGroupItem,
  SandTableType,
  SelectStatus,
  type ViewData,
} from '@types';
import getPanoUrl from '@utils/getPanoUrl';
import { getMediaUrl, getSysUrl } from '@utils/urlMakers';
import { useGate } from 'effector-react';
import { nanoid } from 'nanoid';
// @ts-ignore
import { KrpanoPlayer } from 'pano-sdk';
import { parseJsonString, toFlag } from 'pano-utils';
import { useEffect, useRef } from 'react';
import { useHover } from 'react-use';
import type { SetRequired } from 'type-fest';
import PanelBase from '../../panel/PanelBase';
import { stEditorKrpPlayer } from '../STKrpPlayer';
import fetchSandTableMatrices from '../actions/fetchSandTableMatrices';
import { onMarkerClickGroupMode } from '../actions/onMarkerClick';
import { groupCaptureWidget } from '../components/GroupCaptureWidget';
import { ImageMarkerIcon } from '../components/MarkerIcon';
import { ImageMarkerSubtitle } from '../components/MarkerSubtitle';

interface Props {
  data: STMatrix | STImage;
  index: number;
  onRequestClose: () => void;
}

export function PanelGroupSetting({ data, index, onRequestClose }: Props) {
  const { markers: markerList = [], groupList = [] } = data;
  const item = groupList[index];
  const { state, setState, isChanged } = useTourEditing(item, {
    id: `sg_${nanoid()}`,
    name: '',
    view: { type: 0 },
    markers: { type: 0, list: [] },
    icon: { type: 2, url: sandtableGroupIconList[0].url, id: 0 },
  });
  const { name, view, markers, icon } = state;

  useGate($GateSandtableGroup, { data: state, index });

  useEffect(() => {
    const unwatch = groupCaptureWidget.capture.watch(() => {
      const playerView = stEditorKrpPlayer.getPlayer().getView();
      setState((draft) => {
        draft.view.fov = playerView.fov;
        draft.view.hlookat = playerView.hlookat;
        draft.view.vlookat = playerView.vlookat;
      });
    });
    return () => {
      unwatch();
    };
  }, []);

  useEffect(() => {
    if (view.type === 1) {
      stEditorKrpPlayer.getPlayer().lookTo({
        hlookat: view.hlookat,
        vlookat: view.vlookat,
        fov: view.fov,
      });
    }
  }, []);

  useEffect(() => {
    return onMarkerClickGroupMode.watch((itemId) => {
      handleItemToggle(itemId);
    });
  }, [markers.type, markers.list]);

  const statusAll: SelectStatus = (() => {
    if (markers.type === 1) return SelectStatus.all;
    if (!markers.list.length) return SelectStatus.none;
    return SelectStatus.part;
  })();

  const nameHandler = useInputHandlers((val) => {
    setState((draft) => {
      draft.name = val;
    });
  });

  const handleConfirm = () => {
    $tourDataApi.updateSandtable({
      id: data.id,
      updater: (draft) => {
        draft.groupList ||= [];
        draft.groupList[index] = state;
      },
    });
    onRequestClose();
  };

  const handleDelete = () => {
    $tourDataApi.updateSandtable({
      id: data.id,
      updater: (draft) => {
        draft.groupList ||= [];
        draft.groupList.splice(index, 1);
      },
    });
  };

  const handleSelectAll = (status: SelectStatus) => {
    setState((draft) => {
      draft.markers.type = status === SelectStatus.all ? 0 : 1;
      draft.markers.list = [];
    });
  };

  const handleItemToggle = (id: string) => {
    if (markers.type === 1) {
      setState((draft) => {
        draft.markers.type = 0;
        draft.markers.list = markerList
          .map((x) => x.id)
          .filter((_id) => _id !== id);
      });
    } else {
      setState((draft) => {
        draft.markers.type = 0;
        if (markers.list.includes(id)) {
          draft.markers.list = draft.markers.list.filter((_id) => _id !== id);
        } else {
          draft.markers.list.push(id);
        }
      });
    }
  };

  const handleSelectImages = async () => {
    const { libItems, sysItems } = await selectLibSandtableGroupIcons();
    if (libItems) {
      setState((draft) => {
        draft.icon = {
          ...draft.icon,
          type: 1,
          id: libItems[0].id,
          url: libItems[0].url,
        };
      });
    } else if (sysItems) {
      setState((draft) => {
        draft.icon = {
          ...draft.icon,
          type: 2,
          id: 0,
          url: sysItems[0].url,
        };
      });
    }
  };

  const renderItem = (item: STImageMarker) => {
    const isOn = markers.type === 1 || markers.list.includes(item.id);
    return (
      <div
        key={item.id}
        className="mb-10 flex h-35 w-full cursor-pointer items-center rounded bg-black-#34 px-10"
        onClick={() => handleItemToggle(item.id)}
      >
        <CheckboxNoFunc className="mr-10 shrink-0" checked={isOn} />
        <ImageMarkerIcon marker={item} />
        <div className="mx-10 truncate text-base text-white">{item.title}</div>
        <ImageMarkerSubtitle className="ml-auto" marker={item} />
      </div>
    );
  };

  const renderIcon = () => {
    if (!icon.url) return;
    if (icon.type === 1) {
      return (
        <Image
          src={icon.url}
          type={ImageType.media}
          className="max-h-full max-w-full"
        />
      );
    }
    if (icon.type === 2) {
      return (
        <ImgSVG
          className="w-50 h-50"
          src={getSysUrl(icon.url)}
          style={{
            fill: '#ffffff',
          }}
        />
      );
    }
  };

  return (
    <PanelBase
      isChanged={isChanged}
      title={<Title>分组设置</Title>}
      onRequestClose={onRequestClose}
      onDeleteClick={handleDelete}
      onConfirm={handleConfirm}
    >
      <Collapse title="分组名称">
        <Validator
          value={name}
          validFn={(val) => !!val.trim()}
          warningMessage="请输入分组名称"
        >
          {({ value, warning }) => (
            <Input
              scheme="dark"
              className="h-40 w-full"
              placeholder="请输入分组名称"
              maxLength={50}
              value={value}
              warning={warning}
              {...nameHandler}
            />
          )}
        </Validator>
      </Collapse>
      <Collapse title="分组图标样式">
        <ImageContainer
          className="mt-10 h-115"
          onDeleteClick={() => {
            setState((draft) => {
              draft.icon = {
                ...draft.icon,
                type: 2,
                id: 0,
                url: sandtableGroupIconList[0].url,
              };
            });
          }}
        >
          {renderIcon()}
        </ImageContainer>
        <div className="mt-10 flex items-center justify-between">
          <Tip>建议尺寸:100*100</Tip>
          <Button size="sm" onClick={handleSelectImages}>
            编辑图标
          </Button>
        </div>
        <div className="mt-10 text-orange">
          注：系统分组图标颜色可在"分组高级设置"中配置
        </div>
      </Collapse>
      <Collapse title="分组视角">
        <FeatureToggle
          className="mb-10"
          title="自定义分组视角"
          isOn={view.type === 1}
          onToggle={(bool: boolean) => {
            setState((draft) => {
              draft.view = { type: toFlag(bool) };
            });
          }}
        />
        {!!view.type && <ViewSetting data={data} view={view} />}
        {!!view.type && (
          <FeatureToggle
            title="分组视角缩放"
            isOn={view.changeFov === 1}
            onToggle={(bool: boolean) => {
              setState((draft) => {
                draft.view.changeFov = toFlag(bool);
              });
            }}
          />
        )}
      </Collapse>
      <Collapse title="分组标记点">
        <div className="mb-10 flex h-35 w-full cursor-pointer items-center rounded bg-black-#34 px-10">
          <Checkbox label="全选" status={statusAll} onClick={handleSelectAll} />
        </div>
        {markerList.map(renderItem)}
      </Collapse>
    </PanelBase>
  );
}

const hasView = (
  view: SandTableImageGroupItem['view']
): view is SetRequired<
  SandTableImageGroupItem['view'],
  'hlookat' | 'vlookat' | 'fov'
> => {
  return ![view.hlookat, view.vlookat, view.fov].includes(undefined);
};

function ViewSetting({
  data,
  view,
}: {
  data: STMatrix | STImage;
  view: SandTableImageGroupItem['view'];
}) {
  const previewRef = useRef<KrpPlayer | null>(null);
  useEffect(() => {
    return () => {
      previewRef.current?.removePlayer();
    };
  }, []);

  const hasGroupView = hasView(view);

  useGate(groupCaptureWidget.gate, true);

  useEffect(() => {
    if (!hasGroupView) return;
    if (!previewRef.current) {
      const player = new KrpanoPlayer() as KrpPlayer;
      player.createPlayer('st-group-view', {}, () => {
        previewRef.current = player;
        player.enableControl(false);
        loadScene({
          fov: view.fov,
          hLookAt: view.hlookat,
          vLookAt: view.vlookat,
        });
      });
    } else {
      loadScene({
        fov: view.fov,
        hLookAt: view.hlookat,
        vLookAt: view.vlookat,
      });
    }
  }, [data.id, view]);

  const loadScene = async (
    initView: Pick<ViewData, 'hLookAt' | 'vLookAt' | 'fov'>
  ) => {
    if (!previewRef.current) return;
    if (data.type === SandTableType.image) {
      previewRef.current.loadFlat({
        singleImageUrl: getMediaUrl(data.mediaUrl),
        view: {
          ...viewDefault,
          flatFovType: MatrixFovType.custom,
          ...initView,
        },
      });
    } else if (data.type === SandTableType.matrix) {
      const result = await fetchSandTableMatrices(data.data);
      if (!result[0]) return;
      const { path, multires, cdnDomain, fov } = result[0];
      previewRef.current.loadFlat({
        url: getPanoUrl({ cdnDomain, path }),
        multires: parseJsonString(multires) as PanoMultires,
        matrixFov: parseJsonString(fov) as MatrixFov,
        view: {
          ...viewDefault,
          flatFovType: MatrixFovType.custom,
          ...initView,
        },
      });
    }
  };

  const onTurnViewClick = () => {
    stEditorKrpPlayer.getPlayer().lookTo({
      hlookat: view.hlookat,
      vlookat: view.vlookat,
      fov: view.fov,
    });
  };

  const [container] = useHover((isHovered) => (
    <div className="relative flex h-full w-full items-center justify-center">
      <div id="st-group-view" className="h-full w-full" />
      {isHovered && hasGroupView && (
        <Button
          className="absolute"
          size="sm"
          scheme="gray"
          onClick={onTurnViewClick}
        >
          定位到分组视角
        </Button>
      )}
    </div>
  ));

  return (
    <Validator
      value={view}
      validFn={(value) => {
        if (value.type === 0) return true;
        return hasView(value);
      }}
      warningMessage="请设置分组视角"
    >
      {({ value, warning }) => (
        <ImageContainer
          className="mb-10 h-115"
          placeholder={
            <div className="flex flex-col items-center">
              <p>拖拽缩放图片到合适位置</p>
              <p>点击左侧按钮设置分组视角</p>
            </div>
          }
          warning={warning}
        >
          {hasView(value) && container}
        </ImageContainer>
      )}
    </Validator>
  );
}
